/**
 * Candeza API client — typed fetch wrapper used by every TanStack Query
 * hook in /src/api/*.
 *
 * Responsibilities:
 *  - Inject the current Firebase ID token as `Authorization: Bearer ...`.
 *  - Translate the backend's `{ error: { code, message, details? } }` envelope
 *    into a typed `ApiError` thrown to the caller.
 *  - Centralise auth-failure handling: 401 signs the user out (React Router +
 *    ProtectedRoute handles the /login redirect); 403 surfaces a permission-denied toast.
 *
 * Frontend NEVER calls Firestore directly — all data flows through this
 * client, hitting the FastAPI backend.
 */

import toast from 'react-hot-toast';

import { auth } from '@core/infrastructure/firebase';

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:8000';

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

// ---------------------------------------------------------------------------
// 11E.5 (D9) — owner multi-tenant switcher
// ---------------------------------------------------------------------------
// Module-level "currently selected tenant" set by TenantProvider
// (@core/tenant/TenantContext). Every request carries it as X-Tenant-Id so
// the backend's get_current_user tenant-switch validation (403 outside the
// caller's claims) applies uniformly, without threading tenant state through
// every single API hook call site.
//
// null = no override (backend falls back to the token's own tenant_id claim
// — the correct behaviour for every non-owner and for owners who haven't
// switched). A specific per-call `options.headers['X-Tenant-Id']` (used by
// the Entity Framework's "All" mode fan-out) always wins over this default.
let _activeTenantId: string | null = null;

export function setActiveTenantId(id: string | null): void {
  _activeTenantId = id;
}

export function getActiveTenantId(): string | null {
  return _activeTenantId;
}

/**
 * Explicit per-call `X-Tenant-Id` override, keyed off `useTenant()`'s React
 * state directly rather than the module-level `_activeTenantId` default.
 *
 * Why this exists: `TenantProvider` (an ancestor) pushes a tenant switch
 * into `_activeTenantId` from its OWN `useEffect`, which — because React
 * flushes effects bottom-up (children before parents) — can run AFTER a
 * descendant's `useQuery` has already re-fetched on the new query key,
 * so that fetch would go out under the STILL-STALE previous tenant header.
 * Any hook whose query key includes `selectedTenantId` (so it refetches
 * immediately on switch, before the ancestor's own effect has necessarily
 * run) should pass this instead of relying on the implicit default.
 *
 * Returns `{}` in All mode — no override, falls back to the caller's own
 * home tenant, matching every other "no explicit header" call.
 */
export function tenantHeaderOverride(
  tenantId: string,
  isAllMode: boolean,
): Pick<ApiCallOptions, 'headers'> {
  return isAllMode ? {} : { headers: { 'X-Tenant-Id': tenantId } };
}

export interface FieldError {
  field: string;
  message: string;
}

export interface ErrorBody {
  code: string;
  message: string;
  details?: unknown;
  /** Populated on 422 — one entry per invalid field. Use for inline form errors. */
  field_errors?: FieldError[];
  /** Short correlation ID — also written to the server log. Show to users for support. */
  support_ref?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;
  /** Inline field validation errors (422 only). */
  readonly fieldErrors?: FieldError[];
  /** Server-side correlation ID. Show in "contact support" messages. */
  readonly supportRef?: string;

  constructor(status: number, body: ErrorBody) {
    super(body.message || `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code || 'unknown_error';
    this.details = body.details;
    this.fieldErrors = body.field_errors;
    this.supportRef = body.support_ref;
  }
}

export interface ApiCallOptions {
  /** Optional query-string params (skips undefined/null values). */
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Multipart payload — when set, body must be a FormData. */
  multipart?: boolean;
  /** Skip token injection (used by health checks before login). */
  anonymous?: boolean;
  /** Abort signal forwarded to fetch. */
  signal?: AbortSignal;
  /**
   * Extra request headers. Use for idempotency keys or feature-flag opt-ins.
   * Values are merged AFTER the auth/content-type headers so callers cannot
   * accidentally clobber the Authorization header.
   */
  headers?: Record<string, string>;
}

async function getIdToken(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;
  try {
    return await user.getIdToken();
  } catch {
    return null;
  }
}

function buildUrl(path: string, query?: ApiCallOptions['query']): string {
  const url = new URL(path.replace(/^\//, ''), BASE_URL.replace(/\/?$/, '/'));
  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      url.searchParams.append(key, String(value));
    });
  }
  return url.toString();
}

async function handleAuthFailure(): Promise<void> {
  try {
    await auth.signOut();
  } catch {
    /* swallow — we're already in a failure path */
  }
  // Do NOT hard-redirect with window.location.assign here.
  // That destroys the React component tree (including AuthContext) before
  // resolveUser can set the error message, so the user sees a blank login
  // page with no explanation.
  //
  // Instead: auth.signOut() triggers onAuthStateChanged(null), which sets
  // user=null in AuthContext. ProtectedRoute detects isAuthenticated=false
  // and redirects to /login via React Router, preserving React state and
  // the error message set by resolveUser.
}

/**
 * Generic typed fetch.
 *
 * @example
 *   const me = await apiCall<AuthContext>('GET', '/v1/auth/me');
 *   const created = await apiCall<Student>('POST', '/v1/students', { body: payload });
 */
export async function apiCall<TResponse>(
  method: HttpMethod,
  path: string,
  body?: unknown,
  options: ApiCallOptions = {},
): Promise<TResponse> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };

  if (!options.anonymous) {
    const token = await getIdToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (_activeTenantId) headers['X-Tenant-Id'] = _activeTenantId;
  }

  let payload: BodyInit | undefined;
  if (body !== undefined) {
    if (options.multipart && body instanceof FormData) {
      payload = body; // browser sets Content-Type with boundary
    } else {
      headers['Content-Type'] = 'application/json';
      payload = JSON.stringify(body);
    }
  }

  if (options.headers) {
    for (const [k, v] of Object.entries(options.headers)) {
      // Never let a caller overwrite Authorization — that would be a
      // privilege-escalation footgun.
      if (k.toLowerCase() === 'authorization') continue;
      headers[k] = v;
    }
  }

  const response = await (async () => {
    try {
      return await fetch(buildUrl(path, options.query), {
        method,
        headers,
        body: payload,
        signal: options.signal,
      });
    } catch (err) {
      // AbortError is intentional (user navigated away) — rethrow silently.
      if (err instanceof DOMException && err.name === 'AbortError') throw err;
      // All other network failures (offline, DNS, CORS preflight rejected, etc.)
      // — never let "Failed to fetch" reach the UI raw.
      toast.error('Something went wrong on our side. Please try again.', {
        id: 'candeza-error-toast',
      });
      throw new ApiError(0, { code: 'network_error', message: 'Network request failed' });
    }
  })();

  if (response.status === 204) {
    return undefined as TResponse;
  }

  // Non-JSON responses (PDF, CSV, etc.) — caller passes TResponse=Blob.
  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');

  if (!response.ok) {
    let errBody: ErrorBody = {
      code: `http_${response.status}`,
      message: response.statusText || 'Request failed',
    };
    if (isJson) {
      try {
        const parsed = (await response.json()) as { error?: ErrorBody } | ErrorBody;
        errBody = 'error' in parsed && parsed.error ? parsed.error : (parsed as ErrorBody);
      } catch {
        /* keep fallback */
      }
    }

    if (response.status === 401) {
      await handleAuthFailure();
    } else if (response.status === 403) {
      toast.error(
        errBody.message ? `Permission denied: ${errBody.message}` : 'Permission denied',
        { id: `candeza-error-toast-${errBody.code}` },
      );
    } else if (response.status >= 500) {
      const ref = errBody.support_ref;
      toast.error(
        ref
          ? `Something went wrong on our side. Please try again — if it persists contact support with ref ${ref}.`
          : 'Something went wrong on our side. Please try again.',
        {
          id: `candeza-error-toast`,
          duration: 8000,
          // data-testid anchor for E2E: use the toast id
        },
      );
    } else if (response.status === 422) {
      // 422 field errors are shown inline by forms (via ApiError.fieldErrors).
      // Do NOT toast here — the form component handles the UX.
    } else if (response.status >= 400) {
      toast.error(errBody.message || 'Request failed', {
        id: `candeza-error-toast-${errBody.code}`,
      });
    }

    throw new ApiError(response.status, errBody);
  }

  if (!isJson) {
    return (await response.blob()) as TResponse;
  }

  return (await response.json()) as TResponse;
}

export const api = {
  get: <T>(path: string, opts?: ApiCallOptions) => apiCall<T>('GET', path, undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: ApiCallOptions) =>
    apiCall<T>('POST', path, body, opts),
  patch: <T>(path: string, body?: unknown, opts?: ApiCallOptions) =>
    apiCall<T>('PATCH', path, body, opts),
  put: <T>(path: string, body?: unknown, opts?: ApiCallOptions) =>
    apiCall<T>('PUT', path, body, opts),
  delete: <T>(path: string, opts?: ApiCallOptions) => apiCall<T>('DELETE', path, undefined, opts),
};

